import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Settings, Search, X, Loader2, Bot, MessageCircle, Package, ShoppingCart, BookOpen, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useLang } from '../../i18n/LanguageContext';
import UserMenu from '../UserMenu/UserMenu';
import searchService from '../../api/services/searchService';
import notificationService from '../../api/services/notificationService';
import socketService from '../../api/socket';

export default function Header() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [greeting, setGreeting] = useState('');
  const searchInputRef = useRef(null);
  const notificationsRef = useRef(null);

  // Salutation selon l'heure locale du navigateur
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting(t.header.greetingMorning);
    else if (hour < 18) setGreeting(t.header.greetingAfternoon);
    else setGreeting(t.header.greetingEvening);
  }, [t]);

  // Chargement initial des vraies notifications
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadNotifications = async () => {
      try {
        const res = await notificationService.getNotifications({ limit: 20 });
        setNotifications(res.data || []);
      } catch (err) {
        console.error('Erreur chargement notifications:', err);
      }
    };

    loadNotifications();
  }, [isAuthenticated]);

  // Temps réel : incrémenter le badge sur nouvelle notification socket
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleNewNotification = (notification) => {
      setNotifications(prev => [notification, ...prev]);
    };

    socketService.on('notification', handleNewNotification);
    return () => socketService.off('notification', handleNewNotification);
  }, [isAuthenticated]);

  // Recherche fonctionnelle avec debouncing
  const handleSearch = useCallback(async (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setSearchError(null);

    if (query.length <= 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    setShowSearchResults(true);

    try {
      const results = await searchService.search(query);
      setSearchResults(results.data || []);
    } catch (error) {
      console.error('Erreur recherche:', error);
      setSearchError(t.header.searchError);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Fermer les résultats de recherche au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchResults]);

  const handleSearchSubmit = useCallback((e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSearchResults(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  }, [searchQuery, navigate]);

  const handleResultClick = useCallback((result) => {
    setShowSearchResults(false);
    setSearchQuery('');
    
    // Navigation selon le type de résultat
    switch (result.type) {
      case 'agent':
        navigate(`/agents/${result.id}`);
        break;
      case 'conversation':
        navigate(`/conversations/${result.id}`);
        break;
      case 'product':
        navigate(`/produits/${result.id}`);
        break;
      case 'order':
        navigate(`/commandes/${result.id}`);
        break;
      case 'knowledge':
        navigate(`/base-connaissance/${result.id}`);
        break;
      default:
        navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  }, [searchQuery, navigate]);

  // Gestion des notifications
  const toggleNotifications = useCallback(() => {
    setShowNotifications(prev => !prev);
  }, []);

  const markNotificationAsRead = useCallback(async (id) => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(notif => notif.id === id ? { ...notif, read: true } : notif)
    );
    try {
      await notificationService.markAsRead(id);
    } catch (err) {
      console.error('Erreur markAsRead:', err);
      // Rollback on failure
      setNotifications(prev =>
        prev.map(notif => notif.id === id ? { ...notif, read: false } : notif)
      );
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const previous = notifications;
    setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
    try {
      await notificationService.markAllAsRead();
    } catch (err) {
      console.error('Erreur markAllAsRead:', err);
      setNotifications(previous);
    }
  }, [notifications]);

  // Redirection vers les paramètres
  const handleSettingsClick = useCallback(() => {
    navigate('/settings');
  }, [navigate]);

  // Fermer les notifications au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  const unreadCount = notifications.filter(n => !n.read).length;
  // Backend envoie firstName (pas name ni username)
  const firstName = user?.firstName || user?.email?.split('@')[0] || t.header.defaultUser;

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t.common.justNow;
    if (mins < 60) return t.common.notifAgo(mins);
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t.common.notifHoursAgo(hours);
    return t.common.notifDaysAgo(hours / 24);
  };

  const getResultIcon = (type) => {
    const props = { size: 14, strokeWidth: 1.5 };
    switch (type) {
      case 'agent':       return <Bot {...props} />;
      case 'conversation': return <MessageCircle {...props} />;
      case 'product':     return <Package {...props} />;
      case 'order':       return <ShoppingCart {...props} />;
      case 'knowledge':   return <BookOpen {...props} />;
      default:            return <FileText {...props} />;
    }
  };

  const getResultColor = (type) => {
    switch (type) {
      case 'agent':       return 'rgba(var(--c-accent-green-rgb), 0.12)';
      case 'conversation': return 'rgba(74,144,217,0.12)';
      case 'product':     return 'rgba(229,162,53,0.12)';
      case 'order':       return 'rgba(229,162,53,0.12)';
      default:            return 'rgba(138,138,138,0.12)';
    }
  };

  return (
    <header className="card mb-5 px-5 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <h2 className="font-semibold text-ink truncate" style={{ fontSize: '18px' }}>
          {greeting},{' '}
          <span style={{ color: 'rgb(var(--c-accent-green-rgb))', cursor: 'default' }}>
            {firstName}
          </span>
        </h2>
        <p className="text-label mt-0.5">{t.header.subtitle}</p>
      </div>

      <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
        {/* Barre de recherche fonctionnelle */}
        <div className="relative flex-1 sm:flex-none" ref={searchInputRef}>
          <form onSubmit={handleSearchSubmit}>
            <input
              type="text"
              placeholder={t.header.searchPlaceholder}
              value={searchQuery}
              onChange={handleSearch}
              className="w-full sm:w-64"
              style={{
                background: 'var(--c-primary-dark)',
                color: 'var(--c-ink)',
                border: '0.5px solid var(--c-border)',
                borderRadius: 'var(--radius-md)',
                padding: '0 36px',
                height: '36px',
                fontSize: '13px',
                outline: 'none',
              }}
              onFocus={e => { e.target.style.borderColor = 'rgba(var(--c-accent-green-rgb), 0.5)'; }}
              onBlur={e => { e.target.style.borderColor = 'var(--c-border)'; }}
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-gray" size={18} />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-accent-green animate-spin" size={16} />
            )}
            {searchQuery && !isSearching && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                  setShowSearchResults(false);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-gray hover:text-white"
              >
                <X size={16} />
              </button>
            )}
          </form>

          {/* Dropdown résultats */}
          {showSearchResults && (
            <div
              className="absolute top-full mt-1.5 w-full animate-fadeIn z-50 max-h-80 overflow-y-auto"
              style={{
                background: 'var(--c-primary-card)',
                border: '0.5px solid var(--c-border)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              }}
            >
              {searchError ? (
                <div className="p-4 text-center text-label" style={{ color: 'var(--c-error)' }}>
                  {searchError}
                </div>
              ) : isSearching ? (
                <div className="p-4 text-center text-label flex items-center justify-center gap-2">
                  <Loader2 size={14} className="animate-spin" style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                  {t.header.searching}
                </div>
              ) : searchResults.length === 0 && searchQuery.length > 2 ? (
                <div className="p-4 text-center text-label">
                  {t.header.noResults(searchQuery)}
                </div>
              ) : searchResults.length > 0 ? (
                <div className="py-1">
                  {searchResults.map((result, index) => (
                    <div
                      key={`${result.type}-${result.id}-${index}`}
                      onClick={() => handleResultClick(result)}
                      className="px-3 py-2.5 cursor-pointer flex items-center gap-3"
                      style={{ borderBottom: '0.5px solid var(--c-border-subtle)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--c-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{
                          background: getResultColor(result.type),
                          color: result.type === 'agent' ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-text-gray)',
                        }}
                      >
                        {getResultIcon(result.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink truncate">
                          {result.title || result.name || result.subject || t.common.noTitle}
                        </p>
                        <p className="text-label truncate">
                          {result.description || result.content || result.summary || '—'}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div className="px-3 py-2" style={{ borderTop: '0.5px solid var(--c-border)' }}>
                    <button
                      onClick={handleSearchSubmit}
                      className="w-full text-center text-label py-1"
                      style={{ color: 'rgb(var(--c-accent-green-rgb))', fontSize: '12px' }}
                    >
                      {t.common.seeAllResults}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="relative" ref={notificationsRef}>
          <button
            onClick={toggleNotifications}
            className="relative p-2 rounded-lg"
            style={{ color: 'var(--c-text-gray)' }}
            aria-label={t.header.notifications}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--c-hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <Bell size={18} strokeWidth={1.5} />
            {unreadCount > 0 && (
              <span
                className="absolute top-1 right-1 w-2 h-2 rounded-full"
                style={{ background: 'var(--c-error)' }}
              />
            )}
          </button>

          {/* Panneau des notifications */}
          {showNotifications && (
            <div
              className="absolute right-0 mt-1.5 w-80 max-w-[calc(100vw-2rem)] animate-fadeIn"
              style={{
                background: 'var(--c-primary-card)',
                border: '0.5px solid var(--c-border)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                zIndex: 'var(--z-dropdown)',
              }}
            >
              <div
                className="px-4 py-3 flex items-center justify-between"
                style={{ borderBottom: '0.5px solid var(--c-border)' }}
              >
                <p className="text-sm font-medium text-ink">{t.header.notifications}</p>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-label"
                    style={{ color: 'rgb(var(--c-accent-green-rgb))', fontSize: '11px' }}
                  >
                    {t.header.markAllRead}
                  </button>
                )}
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {notifications.length > 0 ? (
                  notifications.map(notification => {
                    const notifId = notification._id || notification.id;
                    return (
                      <div
                        key={notifId}
                        onClick={() => markNotificationAsRead(notifId)}
                        className={`p-4 border-b border-primary-dark/10 cursor-pointer transition-colors ${
                          !notification.read ? 'bg-accent-green/5' : 'hover:bg-primary-dark/20'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                            notification.type === 'info' ? 'bg-blue-500' :
                            notification.type === 'success' ? 'bg-green-500' :
                            notification.type === 'warning' ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`} />
                          <div className="flex-1 min-w-0">
                            {notification.title && (
                              <p className="text-sm font-medium text-white truncate">{notification.title}</p>
                            )}
                            <p className="text-sm text-white/80">{notification.message}</p>
                            <p className="text-xs text-text-gray mt-1">{formatRelativeTime(notification.createdAt)}</p>
                          </div>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-accent-green rounded-full flex-shrink-0 mt-1" />
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-8 text-center text-text-gray">
                    <Bell size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t.header.noNotifications}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Paramètres */}
        <button
          onClick={handleSettingsClick}
          className="p-2 rounded-lg"
          style={{ color: 'var(--c-text-gray)' }}
          aria-label={t.header.settings}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--c-hover)'; e.currentTarget.style.color = 'rgb(var(--c-accent-green-rgb))'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--c-text-gray)'; }}
        >
          <Settings size={18} strokeWidth={1.5} />
        </button>

        {/* Menu utilisateur avec avatar */}
        <UserMenu />
      </div>
    </header>
  );
}
